import {
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  IconInfoBorderlessSolid,
  Menu,
  Pill,
  Spinner,
  Text,
  ToggleDetails,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { AssessmentPlanService } from "../../../services/AssessmentService";
import { sort } from "../../../utils/utils";
import { Card } from "../../Card";
import { AcademicHierarchySelect } from "../../Selectors/AcademicHierarchySelect/HierarchySelector";
import { MULTI } from "../../Selectors/Selector";

const OfferingPreview = ({
  selectedLos,
  selectedSubjects,
  selectedLevels,
  selectedPrograms,
  subjectList,
  selectedOfferings,
  setSelectedOfferings,
  mapping,
  deployed,
  editingAssessment,
}) => {
  const { t } = useTranslation();
  const [openToggle, setOpenToggle] = useState(false);
  const [offeringList, setOfferingList] = useState({});
  const [filteredOfferingList, setFilteredOfferingList] = useState({});
  const [totalOfferings, setTotalOfferings] = useState(0);
  const [acLevelsWithSubjects, setAcLevelsWithSubjects] = useState([]);
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [selectedHierarchies, setSelectedHierarchies] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState({});

  const [unassociatedSubs, setUnassociatedSubs] = useState({});
  const [filteredUnassociatedSubs, setFilteredUnassociatedSubs] = useState({});
  const [selectedUnassociatedSubs, setSelectedUnassociatedSubs] = useState([]);
  const [showUnassociated, setShowUnassociated] = useState(true);
  const [loadingOfferings, setLoadingOfferings] = useState(false);

  useEffect(() => {
    if (deployed) {
      setOpenToggle((prevOpenToggle) => !prevOpenToggle);
    }
  }, [deployed]);

  useEffect(() => {
    if (
      selectedLos.length > 0 &&
      selectedSubjects.length > 0 &&
      selectedLevels.length > 0
    ) {
      setSelectedHierarchies([]);
      setSelectedSubs({});
      setShowUnassociated(false);
      const acSubs =
        typeof selectedSubjects[0] === "string"
          ? // if it's a list of ids:
            subjectList.filter((subject) =>
              selectedSubjects.includes(subject.id)
            )
          : // if it's a list of objects:
            subjectList.filter((subject) =>
              selectedSubjects.some(
                (selectedSub) => selectedSub.id === subject.id
              )
            );
      const courseCodes = acSubs.map((sub) => sub.title);
      setLoadingOfferings(true);
      AssessmentPlanService.verifyPlanMappings(
        selectedLos,
        courseCodes,
        selectedLevels,
        selectedPrograms,
        mapping
      )
        .then((offerings) => {
          setTotalOfferings(offerings.length);
          const sortedOfferings = sort(offerings, (offering) => offering.code);
          const offeringsObject = {};
          sortedOfferings.forEach((offering) => {
            if (
              !(
                offering.course_summary.academic_subject.name in offeringsObject
              )
            ) {
              offeringsObject[offering.course_summary.academic_subject.name] = [
                offering,
              ];
            } else {
              offeringsObject[
                offering.course_summary.academic_subject.name
              ].push(offering);
            }
          });

          if (!editingAssessment) {
            const selectedOfferingList = {};
            Object.keys(offeringsObject).forEach((c) => {
              selectedOfferingList[c] = [];
              offeringsObject[c].forEach((course) => {
                selectedOfferingList[c].push(course.id);
              });
            });
            setSelectedOfferings(selectedOfferingList);
          }

          setOfferingList(offeringsObject);
          setFilteredOfferingList(offeringsObject);
          setShowUnassociated(true);
        })
        .finally(() => setLoadingOfferings(false));
    } else {
      setSelectedOfferings({});
      setOfferingList({});
      setFilteredOfferingList({});
      setTotalOfferings(0);
    }
  }, [
    selectedLos,
    selectedSubjects,
    selectedLevels,
    selectedPrograms,
    subjectList,
    setSelectedOfferings,
    mapping,
  ]);

  useEffect(() => {
    if (acLevelsWithSubjects.length > 0) {
      const options = hierarchyOptions.map((h) => h.id);
      const hierarchySubObj = {};
      setSelectedHierarchies(options);
      acLevelsWithSubjects.forEach((h) => {
        if (options.includes(h.id)) {
          hierarchySubObj[h.id] = h.children.map((c) => c.name);
        }
      });
      setSelectedSubs(hierarchySubObj);
    }
  }, [hierarchyOptions, acLevelsWithSubjects]);

  useEffect(() => {
    const hierarchySubList = [];
    selectedHierarchies.forEach((h) => {
      const hierarchy = acLevelsWithSubjects.find((s) => s.id === h);
      const subs = selectedSubs[hierarchy.id];
      hierarchySubList.push(...subs);
    });
    const filteredList = {};
    Object.entries(offeringList).forEach(([sub, courses]) => {
      if (hierarchySubList.includes(sub)) {
        filteredList[sub] = courses;
      }
    });
    setFilteredOfferingList(filteredList);
  }, [offeringList, selectedHierarchies, acLevelsWithSubjects, selectedSubs]);

  const handleHierarchySelect = (hierarchyIds) => {
    if (hierarchyIds.length === 0) {
      setShowUnassociated(true);
      setSelectedHierarchies(hierarchyOptions.map((h) => h.id));
    } else {
      setShowUnassociated(false);
      setSelectedHierarchies(hierarchyIds);
    }
  };

  const handleSubjectSelect = (subs, hierarchyId) => {
    setSelectedSubs({ ...selectedSubs, [hierarchyId]: subs });
  };

  const handleUnassociatedSubjectSelect = (subs) => {
    const newSubs = {};
    Object.keys(unassociatedSubs).forEach((sub) => {
      if (subs.includes(sub)) {
        newSubs[sub] = unassociatedSubs[sub];
      }
    });
    setFilteredUnassociatedSubs(newSubs);
    setSelectedUnassociatedSubs(subs);
  };

  // Retrieves subjects that are not associated with any hierarchy
  useEffect(() => {
    if (acLevelsWithSubjects.length > 0) {
      const subjects = [];
      acLevelsWithSubjects.forEach((h) => {
        h.children.forEach((c) => {
          subjects.push(c.name);
        });
      });

      const unassociated = {};
      Object.keys(offeringList).forEach((s) => {
        if (!subjects.includes(s)) {
          unassociated[s] = offeringList[s];
        }
      });
      setUnassociatedSubs(unassociated);
      setFilteredUnassociatedSubs(unassociated);
      setSelectedUnassociatedSubs(Object.keys(unassociated));
    }
  }, [acLevelsWithSubjects, offeringList]);

  return (
    <Card>
      <ToggleDetails
        fluidWidth
        iconPosition="end"
        onToggle={() => setOpenToggle((prevOpenToggle) => !prevOpenToggle)}
        expanded={openToggle}
        summary={
          <Flex gap="small">
            {loadingOfferings ? (
              <Flex.Item>
                <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
              </Flex.Item>
            ) : (
              <Flex.Item padding="0 xx-small 0 0">
                <IconInfoBorderlessSolid size="x-small" color="brand" />
              </Flex.Item>
            )}
            {loadingOfferings ? (
              <Flex.Item shouldShrink>
                <Text as="p">{t("offeringPreview.searching")}</Text>
              </Flex.Item>
            ) : deployed ? (
              <Flex.Item shouldShrink>{t("offeringPreview.heading")}</Flex.Item>
            ) : Object.keys(offeringList).length > 0 ? (
              <Flex.Item shouldShrink>
                <Text as="p">
                  {t("offeringPreview.coursesRelated", { num: totalOfferings })}
                </Text>
                <Text as="p">{t("offeringPreview.reviewCourses")}</Text>
              </Flex.Item>
            ) : (
              <Flex.Item shouldShrink>
                <Text as="p">
                  {t("offeringPreview.coursesRelated", { num: 0 })}
                </Text>
                <Text as="p">{t("offeringPreview.selectCriteria")}</Text>
              </Flex.Item>
            )}
          </Flex>
        }
      >
        {Object.keys(offeringList).length > 0 ? (
          <View as="div" padding="small">
            <div
              style={{
                width: "100%",
                padding: "0 0 15px 0",
                borderBottom: "1px solid #E8E8E8",
              }}
            >
              <AcademicHierarchySelect
                type={MULTI}
                selectHandler={handleHierarchySelect}
                setAcLevelsWithSubjects={setAcLevelsWithSubjects}
                size="small"
                subjects={offeringList}
                setHierarchyOptions={setHierarchyOptions}
              />
            </div>
            <Flex alignItems="start" wrap="wrap" gap="small" padding="small 0">
              <Flex.Item size="40%">
                <Heading
                  as="h4"
                  level="h4"
                  margin="0 0 small 0"
                  border="bottom"
                >
                  {t("academicHierarchy.hierarchies")}
                </Heading>
                <div
                  style={{
                    borderRight: "1px solid #E8E8E8",
                    padding: "5px",
                    height: 400,
                    maxHeight: 400,
                    overflowY: "scroll",
                  }}
                >
                  <Menu width="100%" themeOverride={{ maxWidth: "100%" }}>
                    {selectedHierarchies.map((hierarchyId, key) => {
                      const hierarchy = acLevelsWithSubjects.find(
                        (lvl) => hierarchyId === lvl.id
                      );
                      return (
                        <Menu.Group
                          allowMultiple
                          key={key}
                          label={<Text size="small">{hierarchy.name}</Text>}
                          selected={selectedSubs[hierarchyId].map((s) => s)}
                          onSelect={(_e, subs) =>
                            handleSubjectSelect(subs, hierarchyId)
                          }
                        >
                          {hierarchy.children.map(
                            (sub, key) =>
                              offeringList[sub.name] && (
                                <Menu.Item
                                  key={key}
                                  value={sub.name}
                                  themeOverride={{ padding: "0.1rem 0" }}
                                >
                                  <Flex
                                    justifyItems="space-between"
                                    alignItems="start"
                                  >
                                    <Flex.Item>
                                      <Text size="small">{sub.name}</Text>
                                    </Flex.Item>
                                    <Flex.Item align="start">
                                      <Pill
                                        themeOverride={{
                                          textFontSize: "0.7rem",
                                        }}
                                        margin="0 small 0 0"
                                        color="info"
                                      >
                                        {offeringList[sub.name].length === 1
                                          ? t("offeringPreview.oneCourse")
                                          : t(
                                              "offeringPreview.multipleCourses",
                                              {
                                                num: offeringList[sub.name]
                                                  .length,
                                              }
                                            )}
                                      </Pill>
                                    </Flex.Item>
                                  </Flex>
                                </Menu.Item>
                              )
                          )}
                        </Menu.Group>
                      );
                    })}
                    {showUnassociated &&
                      Object.keys(unassociatedSubs).length > 0 && (
                        <Menu.Group
                          allowMultiple
                          label={
                            <Text size="small">
                              {t("offeringPreview.unassociated")}
                            </Text>
                          }
                          selected={selectedUnassociatedSubs}
                          onSelect={(_e, subs) =>
                            handleUnassociatedSubjectSelect(subs)
                          }
                        >
                          {Object.keys(unassociatedSubs).map(
                            (sub, key) =>
                              !!offeringList[sub] && (
                                <Menu.Item
                                  key={key}
                                  value={sub}
                                  themeOverride={{ padding: "0.1rem 0" }}
                                >
                                  <Flex
                                    justifyItems="space-between"
                                    alignItems="start"
                                  >
                                    <Flex.Item>
                                      <Text size="small">{sub}</Text>
                                    </Flex.Item>
                                    <Flex.Item align="start">
                                      <Pill
                                        themeOverride={{
                                          textFontSize: "0.7rem",
                                        }}
                                        margin="0 small 0 0"
                                        color="info"
                                      >
                                        {offeringList[sub].length === 1
                                          ? t("offeringPreview.oneCourse")
                                          : t(
                                              "offeringPreview.multipleCourses",
                                              { num: offeringList[sub].length }
                                            )}
                                      </Pill>
                                    </Flex.Item>
                                  </Flex>
                                </Menu.Item>
                              )
                          )}
                        </Menu.Group>
                      )}
                  </Menu>
                </div>
              </Flex.Item>
              <Flex.Item shouldGrow>
                <Heading
                  as="h4"
                  level="h4"
                  margin="0 0 small 0"
                  border="bottom"
                >
                  {t("offeringPreview.coursesHeader")}
                </Heading>
                <div
                  style={{
                    paddingLeft: "10px",
                    height: 400,
                    maxHeight: 400,
                    overflowY: "scroll",
                  }}
                >
                  {Object.keys(filteredOfferingList).map((subject, key) => (
                    <View key={key} as="div" margin="0 0 small 0">
                      <CheckboxGroup
                        name={subject}
                        size="small"
                        description={<Text size="small">{subject}</Text>}
                        value={
                          selectedOfferings[subject] &&
                          selectedOfferings[subject].map((id) => id)
                        }
                        onChange={(v) =>
                          setSelectedOfferings({
                            ...selectedOfferings,
                            [subject]: v,
                          })
                        }
                        disabled={deployed}
                      >
                        {filteredOfferingList[subject].map((course, key) => (
                          <Checkbox
                            key={key}
                            label={course.code}
                            value={course.id}
                          />
                        ))}
                      </CheckboxGroup>
                    </View>
                  ))}
                  {showUnassociated &&
                    Object.keys(filteredUnassociatedSubs).map(
                      (subject, key) => (
                        <View key={key} as="div" margin="0 0 small 0">
                          <CheckboxGroup
                            name={subject}
                            size="small"
                            description={<Text size="small">{subject}</Text>}
                            value={
                              selectedOfferings[subject] &&
                              selectedOfferings[subject].map((id) => id)
                            }
                            onChange={(v) =>
                              setSelectedOfferings({
                                ...selectedOfferings,
                                [subject]: v,
                              })
                            }
                            disabled={deployed}
                          >
                            {filteredUnassociatedSubs[subject].map(
                              (course, key) => (
                                <Checkbox
                                  key={key}
                                  label={course.code}
                                  value={course.id}
                                />
                              )
                            )}
                          </CheckboxGroup>
                        </View>
                      )
                    )}
                </div>
              </Flex.Item>
            </Flex>
          </View>
        ) : (
          <View as="div" padding="small">
            <Text as="p">{t("offeringPreview.noCourses")}</Text>
          </View>
        )}
      </ToggleDetails>
    </Card>
  );
};

export default OfferingPreview;
